import React from 'react';
import { motion } from 'framer-motion';
export interface FractionStripProps {
  /** Array of fractions to display as strips for comparison */
  fractions: Array<{
    numerator: number;
    denominator: number;
    label?: string;
  }>;
  /** Optional label above the strips */
  label?: string;
  /** Highlight a specific fraction index */
  highlightIndex?: number;
  /** 'stimulus' = compact, 'support' = educational panel */
  mode?: 'stimulus' | 'support';
}
function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}
const STRIP_COLORS = [{
  bg: 'bg-blue-500',
  light: 'bg-blue-100',
  border: 'border-blue-300',
  text: 'text-blue-800'
}, {
  bg: 'bg-emerald-500',
  light: 'bg-emerald-100',
  border: 'border-emerald-300',
  text: 'text-emerald-800'
}, {
  bg: 'bg-amber-500',
  light: 'bg-amber-100',
  border: 'border-amber-300',
  text: 'text-amber-800'
}, {
  bg: 'bg-purple-500',
  light: 'bg-purple-100',
  border: 'border-purple-300',
  text: 'text-purple-800'
}, {
  bg: 'bg-rose-500',
  light: 'bg-rose-100',
  border: 'border-rose-300',
  text: 'text-rose-800'
}];
function FractionLabel({
  numerator,
  denominator
}: {
  numerator: number;
  denominator: number;
}) {
  return <span className="inline-flex flex-col items-center leading-none mx-1" data-id="element-11667">
      <span className="text-xs font-bold border-b border-current px-0.5" data-id="element-11668">
        {numerator}
      </span>
      <span className="text-xs font-bold px-0.5" data-id="element-11669">{denominator}</span>
    </span>;
}
function Strip({
  numerator,
  denominator,
  label,
  colorIdx,
  isHighlighted,
  delay,
  mode
}: {
  numerator: number;
  denominator: number;
  label?: string;
  colorIdx: number;
  isHighlighted: boolean;
  delay: number;
  mode: 'stimulus' | 'support';
}) {
  const colors = STRIP_COLORS[colorIdx % STRIP_COLORS.length];
  const fillPercent = denominator > 0 ? numerator / denominator * 100 : 0;
  const clampedFill = Math.max(0, Math.min(100, fillPercent));
  return <div className={`space-y-1 ${isHighlighted ? 'ring-2 ring-indigo-400 ring-offset-2 rounded-lg p-1' : ''}`} data-id="element-11670">
      {/* Label */}
      <div className="flex items-center justify-between" data-id="element-11671">
        <div className={`text-xs font-semibold ${colors.text} flex items-center`} data-id="element-11672">
          {label || <span className="flex items-center gap-1" data-id="element-11673">
              <FractionLabel numerator={numerator} denominator={denominator} data-id="element-11674" />
            </span>}
        </div>
        {mode !== 'stimulus' && <span className="text-[10px] text-slate-400" data-id="element-11675">
            {Math.round(clampedFill)}%
          </span>}
      </div>

      {/* Strip bar */}
      <div className={`relative h-7 ${colors.light} rounded-md overflow-hidden border ${colors.border}`} data-id="element-11676">
        {/* Division lines */}
        {Array.from({
        length: denominator - 1
      }, (_, i) => <div key={i} className="absolute inset-y-0 w-px bg-slate-400/40 z-10" style={{
        left: `${(i + 1) / denominator * 100}%`
      }} data-id="element-11677" />)}

        {/* Filled portion */}
        <motion.div initial={{
        width: 0
      }} animate={{
        width: `${clampedFill}%`
      }} transition={{
        delay,
        duration: 0.5,
        ease: 'easeOut'
      }} className={`absolute inset-y-0 left-0 ${colors.bg} rounded-l-md`} data-id="element-11678" />
      </div>
    </div>;
}
export function FractionStrip({
  fractions,
  label,
  highlightIndex,
  mode = 'support'
}: FractionStripProps) {
  const validFractions = (fractions || []).filter(f => isFiniteNumber(f.numerator) && isFiniteNumber(f.denominator) && f.denominator > 0);
  if (validFractions.length === 0) {
    return <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-center" data-id="element-11679">
        <p className="text-red-600 text-sm font-medium" data-id="element-11680">
          Ongeldige breukenstrook configuratie
        </p>
      </div>;
  }
  // Always show the "whole" (1/1) reference strip
  const showReference = validFractions.every(f => f.denominator > 1);
  const strips = <div className="space-y-3" data-id="element-11681">
      {/* Reference strip: 1 geheel */}
      {showReference && <Strip numerator={1} denominator={1} label="1 geheel" colorIdx={-1} isHighlighted={false} delay={0} mode={mode} data-id="element-11682" />}

      {/* Fraction strips */}
      {validFractions.map((f, i) => <Strip key={i} numerator={f.numerator} denominator={f.denominator} label={f.label} colorIdx={i} isHighlighted={highlightIndex === i} delay={(i + 1) * 0.15} mode={mode} data-id="element-11683" />)}
    </div>;
  // Stimulus mode: compact
  if (mode === 'stimulus') {
    return <div className="bg-blue-50 rounded-xl px-5 py-4 border border-blue-200" data-id="element-11684">
        {label && <p className="text-sm font-semibold text-blue-800 mb-3 text-center" data-id="element-11685">
            {label}
          </p>}
        {strips}
      </div>;
  }
  // Support mode: educational panel
  return <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border-2 border-blue-200 shadow-sm" data-id="element-11686">
      {label && <div className="mb-5 text-center" data-id="element-11687">
          <div className="inline-flex items-center gap-2 bg-white/90 rounded-xl px-4 py-2 border border-blue-200 shadow-sm" data-id="element-11688">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" data-id="element-11689" />
            <span className="text-sm font-bold text-blue-900" data-id="element-11690">{label}</span>
          </div>
        </div>}

      <div className="mb-5" data-id="element-11691">{strips}</div>

      {/* Educational explanation */}
      {validFractions.length >= 2 && <div className="bg-white/80 rounded-xl p-4 border border-blue-100 shadow-sm" data-id="element-11692">
          <div className="flex items-start gap-3" data-id="element-11693">
            <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-11694">
              <span className="text-emerald-600 text-sm" data-id="element-11695">💡</span>
            </div>
            <div data-id="element-11696">
              <h4 className="text-sm font-bold text-emerald-900 mb-1" data-id="element-11697">
                Vergelijk de stroken
              </h4>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-11698">
                Kijk naar de gekleurde delen. Hoe meer gekleurd, hoe groter de
                breuk.
                {validFractions.length === 2 && (() => {
              const a = validFractions[0].numerator / validFractions[0].denominator;
              const b = validFractions[1].numerator / validFractions[1].denominator;
              if (Math.abs(a - b) < 0.001) {
                return ' Deze breuken zijn gelijk!';
              }
              return a > b ? ' De bovenste breuk is groter.' : ' De onderste breuk is groter.';
            })()}
              </p>
            </div>
          </div>
        </div>}
    </div>;
}